/**
 * InstrumentDetailPanel - opens when the user clicks an instrument chip
 * (cardiometabolic + longevity expansion 2026-05-07).
 *
 * Anchored to the prototype's PROM detail surface
 * (https://ai-native-prototype.vercel.app/care-pathway-studio.html PROMs
 * tab). The prototype renders the PHQ-9 detail panel with the
 * questionnaire form + Computed Scores section; this build reuses the
 * existing PromCard primitive (Sprint 3.3) for the form and submission
 * flow, then adds the Computed Scores readout when a score is captured.
 *
 * Selection contract:
 *   - `selected` is the active PromId. If `null`, render an empty-state
 *     with a hint to pick an instrument from the schedule.
 *   - On submit, `onSubmitted` is called with the score payload so the
 *     containing view can append it to the displayed Computed Scores
 *     list and forward it to the FhirJsonPreview surface.
 *
 * HC1 reproducibility flows from PromCard (deterministic submission id).
 * HC8 fail-closed flows from PromCard (Submit disabled until every
 *   answer is filled).
 * HC9: PromCard emits the AuditEvent with `source=proms-tab`.
 * HC2 / HC5: token-only styling, no em / en dashes.
 */
import { PromCard, type PromSubmittedResult } from './PromCard';
import {
  PROM_DEFINITION_BY_ID,
  type PromId,
} from './prom-definitions';

export interface InstrumentDetailPanelProps {
  /** Currently selected PROM id, or null for the empty state. */
  selected: PromId | null;
  /**
   * Callback fired when the PromCard submission lands. The PROMs view
   * uses this to refresh the Computed Scores list and the
   * FhirJsonPreview JSON.
   */
  onSubmitted?: (result: PromSubmittedResult) => void;
  /** Optional clock injection forwarded to PromCard for tests. */
  nowIso?: () => string;
  /** Optional class hook for layout-specific overrides. */
  className?: string;
}

export function InstrumentDetailPanel({
  selected,
  onSubmitted,
  nowIso,
  className,
}: InstrumentDetailPanelProps) {
  const definition = selected ? PROM_DEFINITION_BY_ID[selected] : undefined;

  return (
    <section
      aria-labelledby="instrument-detail-title"
      data-testid="instrument-detail-panel"
      data-prom-selected={selected ?? ''}
      className={[
        'flex flex-col gap-gutter-md',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <header className="flex flex-col gap-1">
        <p className="text-signal-xs uppercase tracking-wide text-text-tertiary">
          Instrument Detail
        </p>
        <h3
          id="instrument-detail-title"
          className="text-signal-xl font-semibold text-text-primary"
        >
          {definition ? definition.title : 'Pick an instrument'}
        </h3>
      </header>

      {definition ? (
        <PromCard
          definition={definition}
          nowIso={nowIso}
          onSubmitted={onSubmitted}
        />
      ) : (
        <div
          data-testid="instrument-detail-empty"
          className="flex flex-col gap-gutter-sm rounded-signal-lg border border-dashed border-surface-border bg-surface-muted p-gutter-lg text-signal-sm text-text-secondary"
        >
          <p className="font-semibold text-text-primary">
            No instrument selected
          </p>
          <p>
            Click any instrument chip in the Admin Schedule to open its
            Likert form. Submission writes a QuestionnaireResponse plus
            a LOINC-coded Observation through the FHIR client and emits
            an AuditEvent with source proms-tab.
          </p>
        </div>
      )}
    </section>
  );
}
